package co.maytes.standupbot.dashboard.features.standups

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.EmptyState
import co.maytes.standupbot.dashboard.components.feedback.SkeletonList
import co.maytes.standupbot.dashboard.components.motion.StateSwap
import co.maytes.standupbot.dashboard.components.widgets.Avatar
import co.maytes.standupbot.dashboard.components.widgets.AvatarTone
import co.maytes.standupbot.dashboard.components.widgets.Badge
import co.maytes.standupbot.dashboard.components.widgets.BadgeTone
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.Checkbox
import co.maytes.standupbot.dashboard.components.widgets.IconButton
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideTrash
import co.maytes.standupbot.dashboard.components.widgets.icons.LucideUsers
import co.maytes.standupbot.dashboard.features.roster.ImportChannelModal
import co.maytes.standupbot.dashboard.features.roster.LinkReportState
import co.maytes.standupbot.dashboard.features.roster.MemberLinkChips
import co.maytes.standupbot.dashboard.features.roster.rememberLinkReportStore
import co.maytes.standupbot.dashboard.features.settings.LinkLinearIdentityModal
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.ToastKind
import co.maytes.standupbot.dashboard.stores.rememberAuthStore
import co.maytes.standupbot.dashboard.stores.rememberUiStore
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.auth.RoleDto
import co.maytes.standupbot.shared.dto.standup.LinkStateDto
import co.maytes.standupbot.shared.dto.standup.MemberLinkStatusDto
import co.maytes.standupbot.shared.dto.standup.PatchMemberRequestDto
import co.maytes.standupbot.shared.dto.standup.RosterChangeResultDto
import co.maytes.standupbot.shared.dto.standup.StandupMemberDto
import co.maytes.standupbot.shared.roster.LinkReportText
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.launch

@Composable
fun TeamMembersTab(
    store: StandupsStore,
    workspaceId: String?,
    standupId: String,
) {
    val scope = rememberCoroutineScope()
    val auth = rememberAuthStore()
    val ui = rememberUiStore()
    val directory = rememberSlackDirectoryStore()
    val reportStore = rememberLinkReportStore()
    val members = store.cachedMembers(standupId)
    val fetching = store.isFetchingMembers(standupId)
    val standup = store.cachedDetail(standupId)
    var pickerOpen by remember { mutableStateOf(false) }
    var importOpen by remember { mutableStateOf(false) }
    var resyncing by remember { mutableStateOf(false) }
    var linkLinearFor by remember { mutableStateOf<StandupMemberDto?>(null) }

    LaunchedEffect(workspaceId, standupId) {
        if (!workspaceId.isNullOrBlank() && members == null) {
            store.members(workspaceId, standupId)
        }
    }

    LaunchedEffect(workspaceId, standupId, auth.isAdmin) {
        if (!workspaceId.isNullOrBlank() && auth.isAdmin) reportStore.load(workspaceId, standupId)
    }

    LaunchedEffect(workspaceId, pickerOpen) {
        if (pickerOpen && !workspaceId.isNullOrBlank()) directory.refreshUsers(workspaceId)
    }

    val reportState = reportStore.state

    StateSwap(state = members == null) {
        when {
            members == null && fetching -> SkeletonList(rows = 3, showAvatar = true)
            members == null -> SkeletonList(rows = 3, showAvatar = true)
            else -> Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xs)) {
                IdentitySettingsHint()

                if (auth.isAdmin && reportState is LinkReportState.Ready) {
                    LinkReportHeader(
                        summary = LinkReportText.summary(reportState.report),
                        resyncing = resyncing,
                        onResync = onResync@{
                            if (workspaceId.isNullOrBlank()) return@onResync
                            scope.launch {
                                resyncing = true
                                when (val r = store.resyncIdentityLinks(workspaceId)) {
                                    is Result.Ok -> ui.showToast("Re-synced Linear and GitHub links", ToastKind.Success)
                                    is Result.Err -> ui.showToast("Re-sync failed: ${r.error.displayMessage}", ToastKind.Error)
                                }
                                store.members(workspaceId, standupId)
                                reportStore.load(workspaceId, standupId)
                                resyncing = false
                            }
                        },
                    )
                }

                Row(
                    modifier = Modifier.fillMaxWidth().margin(bottom = Spacing.md),
                    horizontalArrangement = Arrangement.SpaceBetween,
                    verticalAlignment = Alignment.CenterVertically,
                ) {
                    SpanText(
                        text = "${members.size} member${if (members.size == 1) "" else "s"}",
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textSecondary),
                    )
                    if (auth.isAdmin) {
                        Row(horizontalArrangement = Arrangement.spacedBy(Spacing.xs)) {
                            if (standup != null) {
                                Button(
                                    label = "Add everyone in ${standup.slackChannelName?.let { "#$it" } ?: "the channel"}",
                                    variant = ButtonVariant.Secondary,
                                    size = ButtonSize.Sm,
                                    onClick = { importOpen = true },
                                )
                            }
                            Button(
                                label = "Add members",
                                variant = ButtonVariant.Primary,
                                size = ButtonSize.Sm,
                                onClick = { pickerOpen = true },
                            )
                        }
                    }
                }

                if (members.isEmpty()) {
                    EmptyState(
                        title = "No members yet",
                        subtitle = "Add Slack users via the Add members button above — each one's Linear activity feeds this standup.",
                        icon = { LucideUsers(size = 26) },
                    )
                } else {
                    members.forEach { member ->
                        MemberRow(
                            member = member,
                            linkStatus = if (auth.isAdmin) reportStore.statusOf(member.userId) else null,
                            onPickLinear = { linkLinearFor = member },
                            onToggleOptedOut = { optedOut ->
                                if (workspaceId == null) return@MemberRow
                                scope.launch {
                                    store.patchMember(
                                        workspaceId = workspaceId,
                                        standupId = standupId,
                                        userId = member.userId,
                                        patch = PatchMemberRequestDto(optedOutStandup = optedOut),
                                    )
                                }
                            },
                            onRemove = {
                                if (workspaceId == null) return@MemberRow
                                scope.launch {
                                    store.removeMember(workspaceId, standupId, member.userId)
                                    reportStore.load(workspaceId, standupId)
                                }
                            },
                        )
                    }
                }
            }
        }
    }

    if (pickerOpen && !workspaceId.isNullOrBlank()) {
        val currentSlackIds = (members ?: emptyList()).map { it.slackUserId }.toSet()
        SlackUserPickerModal(
            directory = directory,
            alreadyOnTeam = currentSlackIds,
            onConfirm = { adds, removes ->
                pickerOpen = false
                scope.launch {
                    if (adds.isNotEmpty()) {
                        when (val r = store.addMembers(workspaceId, standupId, adds)) {
                            is Result.Ok -> ui.showToast("Added ${adds.size}", ToastKind.Success)
                            is Result.Err -> ui.showToast("Add failed: ${r.error.displayMessage}", ToastKind.Error)
                        }
                    }
                    removes.forEach { slackId ->
                        val userId = (members ?: emptyList()).firstOrNull { it.slackUserId == slackId }?.userId
                        if (userId != null) store.removeMember(workspaceId, standupId, userId)
                    }
                    reportStore.load(workspaceId, standupId)
                }
            },
            onDismiss = { pickerOpen = false },
        )
    }

    if (importOpen && !workspaceId.isNullOrBlank() && standup != null) {
        ImportChannelModal(
            workspaceId = workspaceId,
            channelId = standup.slackChannelId,
            channelName = standup.slackChannelName,
            onConfirm = { slackUserIds, includeGuests ->
                importOpen = false
                scope.launch {
                    when (val r = store.importChannel(workspaceId, standupId, slackUserIds, includeGuests)) {
                        is Result.Ok -> {
                            ui.showToast(r.value.toastText(), ToastKind.Success)
                            reportStore.load(workspaceId, standupId)
                        }
                        is Result.Err -> ui.showToast("Couldn't add the channel: ${r.error.displayMessage}", ToastKind.Error)
                    }
                }
            },
            onDismiss = { importOpen = false },
        )
    }

    linkLinearFor?.let { member ->
        LinkLinearIdentityModal(
            displayName = member.displayName,
            currentLinearUserId = member.linearUserId,
            onSave = { linearUserId ->
                linkLinearFor = null
                if (linearUserId != null && !workspaceId.isNullOrBlank()) {
                    scope.launch {
                        when (val r = store.linkLinearIdentity(workspaceId, standupId, member.userId, linearUserId)) {
                            is Result.Ok -> {
                                ui.showToast("Linked ${member.displayName} to Linear", ToastKind.Success)
                                reportStore.load(workspaceId, standupId)
                            }
                            is Result.Err -> ui.showToast("Couldn't link: ${r.error.displayMessage}", ToastKind.Error)
                        }
                    }
                }
            },
            onDismiss = { linkLinearFor = null },
        )
    }
}

private fun RosterChangeResultDto.toastText(): String =
    buildString {
        append("Added $added")
        if (alreadyPresent > 0) append(", $alreadyPresent already on the roster")
        if (skipped > 0) append(", $skipped skipped")
    }

@Composable
private fun LinkReportHeader(summary: String, resyncing: Boolean, onResync: () -> Unit) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = Spacing.sm)
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.md)
            .backgroundColor(Palette.surface)
            .styleModifier {
                property("border", "1px solid ${Palette.border.cssRgb()}")
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = summary,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.textPrimary),
        )
        Button(
            label = if (resyncing) "Re-syncing…" else "Re-sync",
            variant = ButtonVariant.Secondary,
            size = ButtonSize.Sm,
            enabled = !resyncing,
            onClick = onResync,
        )
    }
}

@Composable
private fun IdentitySettingsHint() {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .margin(bottom = Spacing.sm)
            .padding(topBottom = Spacing.xs, leftRight = Spacing.sm)
            .borderRadius(Radius.md)
            .backgroundColor(Palette.surfaceSubtle)
            .styleModifier {
                property("border", "1px solid ${Palette.borderSubtle.cssRgb()}")
            }
            .gap(Spacing.xxs),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        SpanText(
            text = "Linear and GitHub identities are configured in ",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
        com.varabyte.kobweb.silk.components.navigation.Link(
            path = "/app/settings/members",
            text = "Settings → Members",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .fontWeight(500)
                .color(Palette.accent),
        )
        SpanText(
            text = ".",
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textSecondary),
        )
    }
}

@Composable
private fun MemberRow(
    member: StandupMemberDto,
    linkStatus: MemberLinkStatusDto?,
    onPickLinear: () -> Unit,
    onToggleOptedOut: (Boolean) -> Unit,
    onRemove: () -> Unit,
) {
    val displayName = member.displayName.takeIf { it.isNotBlank() } ?: "@${member.slackUserId}"
    val optedOut = member.optedOutStandup
    val linearNeedsFix = linkStatus != null &&
        (linkStatus.linear == LinkStateDto.MISSING || linkStatus.linear == LinkStateDto.AMBIGUOUS || linkStatus.linear == LinkStateDto.ERROR)

    Row(
        modifier = Modifier
            .fillMaxWidth()
            .padding(topBottom = Spacing.sm, leftRight = Spacing.md)
            .borderRadius(Radius.md)
            .styleModifier {
                property("background-color", (if (optedOut) Palette.surfaceSubtle else Palette.surface).cssRgb())
                property("border", "1px solid ${(if (optedOut) Palette.borderSubtle else Palette.border).cssRgb()}")
                property("gap", "12px")
            },
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Row(
            modifier = Modifier.gap(Spacing.sm).styleModifier {
                property("min-width", "0")
                property("flex", "1")
                property("flex-wrap", "wrap")
            },
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Avatar(
                name = displayName,
                src = member.avatarUrl,
                sizePx = 32,
                tone = if (optedOut) AvatarTone.Muted else AvatarTone.Accent,
            )
            SpanText(
                text = displayName,
                modifier = Modifier
                    .fontSize(Typography.body.size)
                    .fontWeight(600)
                    .color(if (optedOut) Palette.textTertiary else Palette.textPrimary)
                    .styleModifier {
                        property("overflow", "hidden")
                        property("text-overflow", "ellipsis")
                        property("white-space", "nowrap")
                    },
            )
            SpanText(
                text = "@${member.slackUserId}",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary)
                    .styleModifier { property("font-family", Typography.FONT_MONO) },
            )
            if (member.role != RoleDto.MEMBER) {
                Badge(label = member.role.label(), tone = BadgeTone.Accent)
            }
            if (optedOut) {
                Badge(label = "Opted out", tone = BadgeTone.Neutral)
            }
            if (linkStatus != null) MemberLinkChips(status = linkStatus)
        }

        Row(
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(Spacing.md),
        ) {
            if (linearNeedsFix) {
                Button(
                    label = "Pick person",
                    variant = ButtonVariant.Secondary,
                    size = ButtonSize.Sm,
                    onClick = onPickLinear,
                )
            }
            Row(
                modifier = Modifier.gap(Spacing.xs),
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Checkbox(
                    checked = !optedOut,
                    onCheckedChange = { onToggleOptedOut(!it) },
                )
                SpanText(
                    text = if (optedOut) "Opted out" else "Active",
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .color(if (optedOut) Palette.textTertiary else Palette.textPrimary),
                )
            }

            IconButton(
                ariaLabel = "Remove $displayName",
                modifier = Modifier.color(Palette.textTertiary),
                onClick = { onRemove() },
            ) {
                LucideTrash(size = 16, strokeColor = Palette.textTertiary.cssRgb())
            }
        }
    }
}

private fun RoleDto.label(): String = when (this) {
    RoleDto.OWNER -> "Owner"
    RoleDto.ADMIN -> "Admin"
    RoleDto.MEMBER -> "Member"
    RoleDto.VIEWER -> "Viewer"
}
